import React, { Component } from 'react';
import styled from 'styled-components/macro';
import { WithTranslation, withTranslation } from 'react-i18next';

import { colors, zIndex } from '../../styles/constants';

export type OnChangeFunction = (event: Event) => void;

export interface Option {
    label: string;
    value: string;
    onClick: (value: string) => void;
}

interface DropdownMenuProps extends WithTranslation {
    initialOpen: boolean;
    label: any;
    title: string;
    value: string;
    options: Option[];
    disabled?: boolean;
}

interface FilterSelectState {
    open: boolean;
}

class DropdownMenu extends Component<DropdownMenuProps, FilterSelectState> {
    wrapperRef: any = null;

    constructor(props: DropdownMenuProps, context: any) {
        super(props, context);
        this.state = {
            open: props.initialOpen,
        };
        this.toggleOpen = this.toggleOpen.bind(this);
        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.wrapperRef = React.createRef();
    }
    componentDidMount() {
        document.addEventListener('mousedown', this.handleClick);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClick);
    }

    setWrapperRef(node: any) {
        this.wrapperRef = node;
    }

    handleClick(event: any) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.setState({ open: false });
        }
    }

    toggleOpen() {
        this.setState({ open: !this.state.open });
    }

    render() {
        const { label, title, disabled, options, value } = this.props;
        const { open } = this.state;

        return (
            <Wrapper ref={this.setWrapperRef}>
                <MenuButton onClick={this.toggleOpen} disabled={disabled} className={open ? 'open' : ''}>
                    {label}
                </MenuButton>
                <Collapsable open={open}>
                    <OptionsContainer>
                        <OptionsTitle>{title}</OptionsTitle>
                        {options.map((option) => (
                            <OptionButton
                                className={value === option.value ? 'selected' : ''}
                                key={option.value}
                                name={option.value}
                                onClick={() => {
                                    option.onClick(option.value);
                                    this.setState({ open: false });
                                }}
                            >
                                {option.label}
                            </OptionButton>
                        ))}
                    </OptionsContainer>
                </Collapsable>
            </Wrapper>
        );
    }
}

const MenuButton = styled.button`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border: none;
    background: transparent;
    padding: 8px;
    cursor: pointer;
    &:focus {
        outline: none;
    }

    &.open {
        box-shadow: 0 0px 4px rgba(0, 0, 0, 0.2);
        border-radius: 4px;
    }
`;

const Wrapper = styled.div`
    position: relative;
`;

interface CollapsableProps {
    open: boolean;
}

const Collapsable = styled.div<CollapsableProps>`
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 32px;
    right: 0;
    z-index: ${zIndex.top};
    background-color: ${colors.background};
    box-shadow: 0 0px 4px rgba(0, 0, 0, 0.2);
    transition: transform 150ms ease-out;
    transform-origin: top center;
    ${(props) => (props.open ? 'transform: scaleY(1);' : 'transform: scaleY(0);')}
    white-space: nowrap;
    border-radius: 4px;

    &:after {
        content: '';
        position: absolute;
        right: 0;
        top: -3px;
        width: 32px;
        height: 5px;
        background: #fff;
        clear: both;
    }
`;

const OptionButton = styled.button`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border: none;
    background: transparent;
    padding: 8px;
    cursor: pointer;
    &:focus {
        outline: none;
    }

    &:hover {
        background: ${colors.interactiveBackgroundHover};
    }

    &.selected {
        background: ${colors.interactiveBackgroundHover};
    }
    display: block;
    text-align: right;
    width: 100%;
    border-top: 1px solid ${colors.borderLight};
    min-width: 100px;
`;

const OptionsContainer = styled.div``;
const OptionsTitle = styled.div`
    text-transform: uppercase;
    font-size: 9px;
    font-weight: 700;
    color: ${colors.text};
    padding: 8px;
    text-align: center;
`;

export default withTranslation()<React.ComponentType<DropdownMenuProps>>(DropdownMenu);
